<template>
  <nav v-if="headings.length > 0" class="content-toc">
    <h4 class="content-toc__title">{{ $t('table_of_contents') }}</h4>
    <ul class="content-toc__list">
      <li
        v-for="heading in headings"
        :key="heading.id"
        :class="[
          'content-toc__item',
          `content-toc__item--h${heading.level}`,
          { 'content-toc__item--active': heading.id === activeId },
        ]"
      >
        <a :href="`#${heading.id}`" @click.prevent="scrollToHeading(heading.id)">
          {{ heading.text }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
import { ref, watch, onUnmounted, nextTick } from 'vue';

export default {
  name: 'ContentTOC',
  props: {
    content: { type: String, default: '' },
    containerClass: { type: String, required: true },
  },
  setup(props) {
    const headings = ref([]);
    const activeId = ref('');
    let observer = null;

    const extractHeadings = (htmlString) => {
      if (!htmlString) return [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const nodes = doc.querySelectorAll('h2, h3');
      return Array.from(nodes).map((node, index) => ({
        id: `toc-heading-${index}`,
        text: node.textContent.trim(),
        level: parseInt(node.tagName.charAt(1)),
      }));
    };

    const injectIds = () => {
      const container = document.querySelector(`.${props.containerClass}`);
      if (!container) return;
      const nodes = container.querySelectorAll('h2, h3');
      nodes.forEach((node, index) => {
        node.id = `toc-heading-${index}`;
      });
    };

    const setupObserver = () => {
      if (observer) observer.disconnect();
      const elements = document.querySelectorAll('[id^="toc-heading-"]');
      if (!elements.length) return;
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            activeId.value = visible[0].target.id;
          }
        },
        { rootMargin: '-10% 0% -70% 0%', threshold: 0 }
      );
      elements.forEach((el) => observer.observe(el));
    };

    const scrollToHeading = (id) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    watch(
      () => props.content,
      async (newContent) => {
        if (!newContent) return;
        headings.value = extractHeadings(newContent);
        await nextTick();
        injectIds();
        setupObserver();
      },
      { immediate: true }
    );

    onUnmounted(() => {
      if (observer) observer.disconnect();
    });

    return { headings, activeId, scrollToHeading };
  },
};
</script>

<style scoped>
.content-toc {
  background: var(--branco);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 3px solid var(--azul-medio);
}

.content-toc__title {
  font-family: var(--font-freight);
  font-weight: var(--weight-bold);
  font-size: 13px;
  color: var(--azul-escuro);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 12px 0;
}

.content-toc__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.content-toc__item {
  margin-bottom: 2px;
}

.content-toc__item a {
  display: block;
  font-family: var(--font-freight);
  font-size: 13px;
  color: var(--cor-textos);
  text-decoration: none;
  padding: 5px 6px;
  border-radius: 4px;
  line-height: 1.4;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.content-toc__item a:hover {
  background-color: var(--fundos);
  color: var(--azul-medio);
}

.content-toc__item--h3 a {
  padding-left: 18px;
  font-size: 12px;
}

.content-toc__item--active a {
  color: var(--azul-medio);
  font-weight: var(--weight-semibol);
  background-color: rgba(191, 226, 247, 0.4);
}

@media (max-width: 1024px) {
  .content-toc {
    display: none;
  }
}
</style>
